import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../components/icons/Icon";

/**
 * Upgrade-required modal (user-approved policy 1 — min-client-version
 * gate). Terminal, BLOCKING surface that informs the caller their
 * client build is too old for the server and the only path forward is
 * a refresh onto the freshly-deployed build.
 *
 * Mounted by the App-level gate the moment `useUIStore.upgradeRequired`
 * latches — set by either the HTTP `426 Upgrade Required` handler
 * (`session/authed_request.ts`) or the WS upgrade-required close code /
 * protocol-major mismatch (`ws/client.ts`). DISTINCT from the displaced
 * "active elsewhere" modal + the ordinary disconnect banner so the user
 * gets a clear, non-cryptic instruction instead of a silent logout.
 *
 * The reload path reuses the PWA service-worker update when one is
 * waiting (so the refresh actually lands the new precached shell rather
 * than a stale cached one); otherwise it falls back to a hard
 * `window.location.reload()`. Both are reachable from this state per
 * the policy brief ("Ensure the PWA SW update path is reachable").
 */

export interface UpgradeRequiredModalProps {
  /** Trigger the reload / SW-update apply. The App wires this to the
   *  PWA `applyUpdate()` when a worker is waiting, else a hard reload.
   *  Fires at most once (re-clicks are absorbed by the latched flag +
   *  the in-flight reload). */
  readonly onReload: () => void;
}

export function UpgradeRequiredModal(
  props: UpgradeRequiredModalProps,
): JSX.Element | null {
  const { t } = useTranslation();
  const buttonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    buttonRef.current?.focus();
  }, []);

  if (typeof document === "undefined") return null;

  return createPortal(
    <div
      className="system-modal upgrade-required-modal"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="upgrade-required-title"
      aria-describedby="upgrade-required-body"
      data-testid="upgrade-required-modal"
    >
      <div className="system-modal__card">
        <div className="system-modal__icon" aria-hidden="true">
          <Icon name="arrow-up" size={28} />
        </div>
        <h2 id="upgrade-required-title" className="system-modal__title">
          {t("app.upgrade_required_title")}
        </h2>
        <p
          id="upgrade-required-body"
          className="system-modal__body"
          data-testid="upgrade-required-body"
        >
          {t("app.upgrade_required_body")}
        </p>
        <button
          ref={buttonRef}
          type="button"
          className="btn system-modal__cta"
          onClick={props.onReload}
          data-testid="upgrade-required-cta"
        >
          {t("app.upgrade_required_cta")}
        </button>
      </div>
    </div>,
    document.body,
  );
}
